import { FormAction, GeographicParameterType } from 'types/firmInfo';
import * as Yup from 'yup';

import {
  ACTIVE,
  BAR_NUMBER_REQUIRED_MESSAGE,
  FIELD_REQUIRED_MESSAGE,
  PHONE_REGEX,
  REGISTER,
} from 'utils/constants';

import { addressSchema } from 'components/pages/check-eligibility/newUserSchema';

// validate that there is at least one service type or special service.
const validateAtLeastOneService = function (this: Yup.TestContext) {
  const { serviceTypes, specialServices, status } = this.parent || {};
  if (status !== ACTIVE) {
    return true;
  }
  return (
    (serviceTypes && serviceTypes.length > 0) || (specialServices && specialServices.length > 0)
  );
};

const baseOfficeSchemaObject = {
  email: Yup.string().email('Please enter a valid email address').required(FIELD_REQUIRED_MESSAGE),
  serviceInfoConfirmed: Yup.boolean().when(['forConfirmationModal'], {
    is: true,
    then: Yup.boolean().isTrue('Please confirm that this information is correct before continuing'),
  }),
  addressInfoConfirmed: Yup.boolean().when(['forConfirmationModal'], {
    is: true,
    then: Yup.boolean().isTrue('Please confirm that this information is correct before continuing'),
  }),
  languages: Yup.array().min(1, 'Please select at least one language'),
  serviceTypes: Yup.array()
    .nullable()
    .test(
      'at-least-one-service',
      'At least one service or special service must be selected',
      validateAtLeastOneService,
    ),
  specialServices: Yup.array().nullable(),
  engagementScore: Yup.number()
    .min(0, 'Must be at least 0')
    .max(10, '10 is the highest possible score')
    .required(FIELD_REQUIRED_MESSAGE),
  attorneyInfos: Yup.array().when(['status'], {
    is: (status: string) => status === ACTIVE,
    then: Yup.array().min(1, 'At least one attorney must be assigned to the office.'),
  }),
};

export const firmSchema = Yup.object().shape({
  name: Yup.string().required(FIELD_REQUIRED_MESSAGE),
  mlpStatus: Yup.string().when(['action'], {
    is: (action: FormAction) => action === REGISTER,
    then: Yup.string().required(FIELD_REQUIRED_MESSAGE),
  }),
  email: Yup.string().email('Please enter a valid email address').required(FIELD_REQUIRED_MESSAGE),
  phone: Yup.string()
    .matches(PHONE_REGEX, 'Enter a valid 10 digit phone number')
    .required(FIELD_REQUIRED_MESSAGE),
  locationCode: Yup.string().required(FIELD_REQUIRED_MESSAGE),
  attorneyCode: Yup.number()
    .min(9999)
    .max(100000)
    .nullable(true)
    .transform((_, val) => (val ? Number(val) : null)),
  malpracticeExpireDate: Yup.date().when(['action'], {
    is: (action: FormAction) => action === REGISTER,
    then: Yup.date().required(FIELD_REQUIRED_MESSAGE),
    otherwise: Yup.date().nullable(true),
  }),
  chosenNoCulturalFluency: Yup.boolean(),
  culturalFluency: Yup.array().when(['chosenNoCulturalFluency', 'action'], {
    is: (chosenNoCulturalFluency: boolean, action: FormAction) =>
      !chosenNoCulturalFluency && action !== REGISTER,
    then: Yup.array().min(1, FIELD_REQUIRED_MESSAGE),
  }),
  firmStatusReason: Yup.string().max(64, 'Firm Change Reason must be 64 characters or less'),
  overpaymentDeductionPercentage: Yup.number()
    .integer('Percentage must be a whole number')
    .min(0)
    .max(100),
  feeScheduleAdjustmentPercentage: Yup.number()
    .integer('Percentage must be a whole number')
    .min(0)
    .max(99)
    .test(
      'positive-percentage-for-uploaded-document',
      'A positive adjustment percentage is required when a fee schedule document is uploaded',
      function (percentage) {
        return !this.parent.feeScheduleAdjustmentDocument || Number(percentage) > 0;
      },
    ),
  feeScheduleAdjustmentDocument: Yup.mixed().when(
    ['feeScheduleAdjustmentPercentage', 'feeScheduleAdjustmentDocumentFilename'],
    {
      is: (percentage, documentFilename) => Number(percentage) > 0 && !documentFilename,
      then: Yup.mixed().required(
        'A fee schedule document is required when the adjustment percentage is greater than 0',
      ),
      otherwise: Yup.mixed().nullable(),
    },
  ),
});

export const attorneySchema = Yup.object().shape({
  user: Yup.object().shape({
    firstName: Yup.string().required(FIELD_REQUIRED_MESSAGE),
    lastName: Yup.string().required(FIELD_REQUIRED_MESSAGE),
  }),
  lawSchool: Yup.string().required(FIELD_REQUIRED_MESSAGE),
  phone: Yup.string()
    .matches(PHONE_REGEX, 'Enter a valid 10 digit phone number')
    .required(FIELD_REQUIRED_MESSAGE),
  contactEmail: Yup.string()
    .email('Please enter a valid email address')
    .required(FIELD_REQUIRED_MESSAGE),
  canTravel: Yup.boolean().required(FIELD_REQUIRED_MESSAGE),
  availableWeekends: Yup.boolean().required(FIELD_REQUIRED_MESSAGE),
  availableEvenings: Yup.boolean().required(FIELD_REQUIRED_MESSAGE),
  availableVideoConference: Yup.boolean().required(FIELD_REQUIRED_MESSAGE),
  languages: Yup.array().min(1, 'Please enter at least one language').of(Yup.string()),
  gender: Yup.string().required(FIELD_REQUIRED_MESSAGE),
  noResponse: Yup.boolean(),
  raceOrEthnicity: Yup.array().when(['noResponse'], {
    is: false,
    then: Yup.array().min(1, FIELD_REQUIRED_MESSAGE),
    otherwise: Yup.array(),
  }),
  isPanelUser: Yup.boolean(),
  licenseInfo: Yup.array()
    .min(1, BAR_NUMBER_REQUIRED_MESSAGE)
    .of(
      Yup.object().shape({
        barNumber: Yup.string().required(FIELD_REQUIRED_MESSAGE),
        operatingState: Yup.string().required(FIELD_REQUIRED_MESSAGE),
        issuedDate: Yup.date()
          .min(
            new Date(new Date().setFullYear(new Date().getFullYear() - 100)),
            `Please provide a date within the last 100 years.`,
          )
          .max(new Date(), `Please provide only past dates.`)
          .required(FIELD_REQUIRED_MESSAGE)
          .nullable(),
      }),
    ),
});

export const officeSchema = addressSchema.shape({
  numberOfAttorneys: Yup.number().required(FIELD_REQUIRED_MESSAGE),
  phone: Yup.string()
    .matches(PHONE_REGEX, 'Enter a valid 10 digit phone number')
    .required(FIELD_REQUIRED_MESSAGE),
  ...baseOfficeSchemaObject,
});

export const negotiatedFeeSchema = Yup.object().shape({
  serviceTypeId: Yup.number().when(['feeType'], {
    is: 'serviceMaximum',
    then: Yup.number().required(FIELD_REQUIRED_MESSAGE),
  }),
  feeType: Yup.string().required(FIELD_REQUIRED_MESSAGE),
  feeMaximum: Yup.number().min(1, 'Must be greater than $0'),
  offices: Yup.array().min(1, 'At least one office must be selected'),
  document: Yup.array().of(Yup.object()),
});

export const virtualOfficeSchema = Yup.object().shape({
  phone: Yup.string().matches(PHONE_REGEX, 'Enter a valid 10 digit phone number'),
  geographicParameterType: Yup.string().oneOf(['entireState', 'radius']),
  state: Yup.string().when(['geographicParameterType'], {
    is: (geographicParameterType: GeographicParameterType) =>
      geographicParameterType === 'entireState',
    then: Yup.string().required(FIELD_REQUIRED_MESSAGE),
    otherwise: Yup.string(),
  }),
  zipCode: Yup.string().when(['geographicParameterType'], {
    is: (geographicParameterType: GeographicParameterType) => geographicParameterType === 'radius',
    then: Yup.string().required(FIELD_REQUIRED_MESSAGE),
    otherwise: Yup.string(),
  }),
  radius: Yup.string().when(['geographicParameterType'], {
    is: (geographicParameterType: GeographicParameterType) => geographicParameterType === 'radius',
    then: Yup.string().nullable().required(FIELD_REQUIRED_MESSAGE),
    otherwise: Yup.string().nullable(),
  }),
  ...baseOfficeSchemaObject,
});
